import { fetchApi } from "./api";

/**
 * Site settings: the editable facts the public site and the notification /
 * email plumbing used to hard-code. Six groups, each saved on its own
 * (admin-api `updateSettings { group, value }`). Secrets (the Shopify Admin
 * token, the Turnstile secret key) are never part of a group: they are
 * written through their own admin actions and only their status is read back.
 *
 * The DEFAULTS below are the exact values the site shipped with, so a fresh
 * database renders identically to the pre-settings build until an admin edits
 * something. Keep them in step with the backend seed.
 */

export type SettingsGroup =
  | "org"
  | "notifications"
  | "email"
  | "seo"
  | "integrations"
  | "analytics";

export type OrgSettings = {
  companyName: string;
  legalName: string;
  addressLines: string[];
  phone: string;
  generalEmail: string;
  researchEmail: string;
  sponsorshipEmail: string;
  incubatorEmail: string;
};

export type HealthAlertMode = "always" | "on_change";

export type NotificationSettings = {
  contactRecipients: string[];
  researchRecipients: string[];
  incubatorRecipients: string[];
  newsletterSignupRecipients: string[];
  healthAlertRecipients: string[];
  healthAlertMode: HealthAlertMode;
};

export type EmailSettings = {
  senderName: string;
  senderAddress: string;
  replyTo: string;
  subscribeSubject: string;
  newsletterFooter: string;
};

export type SocialLinks = {
  linkedin: string;
  x: string;
  facebook: string;
  instagram: string;
  youtube: string;
};

export type SeoSettings = {
  titleSuffix: string;
  defaultDescription: string;
  defaultShareImage: string;
  social: SocialLinks;
  searchConsoleTag: string;
};

/** Which public forms require a Cloudflare Turnstile token. */
export type TurnstileEnabledForms = { contact: boolean; newsletter: boolean };

export type IntegrationSettings = {
  /** Turnstile site key (public; rendered into the widget). The secret is write-only. */
  turnstileSiteKey: string;
  turnstileEnabledForms: TurnstileEnabledForms;
};

export type AnalyticsSettings = {
  /** "G-XXXXXXXX"; empty = analytics off. */
  ga4MeasurementId: string;
  /** Plausible `data-domain`; empty = no Plausible script. */
  plausibleDomain: string;
  /** Ask before loading any analytics script. */
  consentBannerEnabled: boolean;
  consentText: string;
  /** Skip /admin and /auth pages. */
  excludeAdmin: boolean;
};

export type SettingsValues = {
  org: OrgSettings;
  notifications: NotificationSettings;
  email: EmailSettings;
  seo: SeoSettings;
  integrations: IntegrationSettings;
  analytics: AnalyticsSettings;
};

export type SettingsGroupValue<G extends SettingsGroup> = SettingsValues[G];

/** admin-api `getSettings` / `updateSettings` response. */
export type SettingsResponse = SettingsValues & {
  /** ISO timestamp of the last save, per group (absent = never saved). */
  updatedAt: Partial<Record<SettingsGroup, string>>;
  /** Whether `email.senderAddress` is a verified Amazon SES identity. */
  senderVerified: boolean;
};

/** content-api `getPublicSettings` response: the subset the public site renders. */
export type PublicSettings = {
  org: OrgSettings;
  seo: SeoSettings;
  email: { senderName: string };
  analytics: AnalyticsSettings;
  integrations: IntegrationSettings;
};

/* ---------------------------------------------------- integration status */

export type ProbeResult = { ok: boolean; detail: string; checkedAt: string };

/** admin-api `getIntegrationStatus` response. */
export type IntegrationStatus = {
  shopify: {
    storeDomain: string;
    apiVersion: string;
    storefront: ProbeResult;
    admin: {
      configured: boolean;
      /** null = never probed (token not configured). */
      ok: boolean | null;
      detail: string;
      lastChangedAt: string | null;
    };
  };
  turnstile: {
    siteKeyConfigured: boolean;
    secretConfigured: boolean;
    lastChangedAt: string | null;
  };
  analytics: { ga4: boolean; plausible: boolean };
};

export type IntegrationTestKind = "shopify-storefront" | "shopify-admin" | "turnstile";

/* ------------------------------------------------------------- defaults */

const DEFAULT_RECIPIENTS = ["nnolasco@advarion.com", "lmorvant@miinews.com"];

export const DEFAULT_ORG: OrgSettings = {
  companyName: "Medical Insight, Inc.",
  // Matches the footer copyright line the site shipped with.
  legalName: "Medical Insight Incorporated",
  addressLines: [],
  phone: "",
  generalEmail: "info@miinews.com",
  researchEmail: "lmorvant@miinews.com",
  sponsorshipEmail: "info@miinews.com",
  incubatorEmail: "info@miinews.com",
};

export const DEFAULT_NOTIFICATIONS: NotificationSettings = {
  contactRecipients: [...DEFAULT_RECIPIENTS],
  researchRecipients: [...DEFAULT_RECIPIENTS],
  incubatorRecipients: [...DEFAULT_RECIPIENTS],
  newsletterSignupRecipients: [...DEFAULT_RECIPIENTS],
  healthAlertRecipients: [...DEFAULT_RECIPIENTS],
  // Today's scheduled job emails on every failing run.
  healthAlertMode: "always",
};

export const DEFAULT_EMAIL: EmailSettings = {
  senderName: "MII News",
  senderAddress: "nnolasco@advarion.com",
  replyTo: "",
  subscribeSubject: "Sign Me Up",
  newsletterFooter: "",
};

export const DEFAULT_SEO: SeoSettings = {
  titleSuffix: "Medical Insight",
  // The site-wide fallback from web/index.html.
  defaultDescription:
    "Medical Insight, Inc. — independent market research, news and industry intelligence for the aesthetic medicine and cosmetic surgery industry since 1993.",
  defaultShareImage: "",
  social: { linkedin: "", x: "", facebook: "", instagram: "", youtube: "" },
  searchConsoleTag: "",
};

export const DEFAULT_INTEGRATIONS: IntegrationSettings = {
  turnstileSiteKey: "",
  turnstileEnabledForms: { contact: false, newsletter: false },
};

export const DEFAULT_ANALYTICS: AnalyticsSettings = {
  ga4MeasurementId: "",
  plausibleDomain: "",
  consentBannerEnabled: false,
  consentText: "We use cookies to measure site traffic. No personal data is sold.",
  excludeAdmin: true,
};

export const DEFAULT_SETTINGS: SettingsValues = {
  org: DEFAULT_ORG,
  notifications: DEFAULT_NOTIFICATIONS,
  email: DEFAULT_EMAIL,
  seo: DEFAULT_SEO,
  integrations: DEFAULT_INTEGRATIONS,
  analytics: DEFAULT_ANALYTICS,
};

export const DEFAULT_PUBLIC_SETTINGS: PublicSettings = {
  org: DEFAULT_ORG,
  seo: DEFAULT_SEO,
  email: { senderName: DEFAULT_EMAIL.senderName },
  analytics: DEFAULT_ANALYTICS,
  integrations: DEFAULT_INTEGRATIONS,
};

/* ---------------------------------------------------------- normalizing */

const str = (v: unknown, fallback: string): string => (typeof v === "string" ? v : fallback);
const bool = (v: unknown, fallback: boolean): boolean => (typeof v === "boolean" ? v : fallback);

const list = (v: unknown, fallback: string[]): string[] => {
  if (Array.isArray(v)) return v.map((s) => String(s).trim()).filter(Boolean);
  if (typeof v === "string") return v.split(/[\r\n,;]+/).map((s) => s.trim()).filter(Boolean);
  return [...fallback];
};

function normalizeOrg(raw: Partial<OrgSettings> | null | undefined): OrgSettings {
  const r = raw ?? {};
  return {
    companyName: str(r.companyName, DEFAULT_ORG.companyName),
    legalName: str(r.legalName, DEFAULT_ORG.legalName),
    addressLines: list(r.addressLines, DEFAULT_ORG.addressLines),
    phone: str(r.phone, DEFAULT_ORG.phone),
    generalEmail: str(r.generalEmail, DEFAULT_ORG.generalEmail),
    researchEmail: str(r.researchEmail, DEFAULT_ORG.researchEmail),
    sponsorshipEmail: str(r.sponsorshipEmail, DEFAULT_ORG.sponsorshipEmail),
    incubatorEmail: str(r.incubatorEmail, DEFAULT_ORG.incubatorEmail),
  };
}

function normalizeNotifications(
  raw: Partial<NotificationSettings> | null | undefined,
): NotificationSettings {
  const r = raw ?? {};
  return {
    contactRecipients: list(r.contactRecipients, DEFAULT_NOTIFICATIONS.contactRecipients),
    researchRecipients: list(r.researchRecipients, DEFAULT_NOTIFICATIONS.researchRecipients),
    incubatorRecipients: list(r.incubatorRecipients, DEFAULT_NOTIFICATIONS.incubatorRecipients),
    newsletterSignupRecipients: list(
      r.newsletterSignupRecipients,
      DEFAULT_NOTIFICATIONS.newsletterSignupRecipients,
    ),
    healthAlertRecipients: list(r.healthAlertRecipients, DEFAULT_NOTIFICATIONS.healthAlertRecipients),
    healthAlertMode: r.healthAlertMode === "on_change" ? "on_change" : "always",
  };
}

function normalizeEmail(raw: Partial<EmailSettings> | null | undefined): EmailSettings {
  const r = raw ?? {};
  return {
    senderName: str(r.senderName, DEFAULT_EMAIL.senderName),
    senderAddress: str(r.senderAddress, DEFAULT_EMAIL.senderAddress),
    replyTo: str(r.replyTo, DEFAULT_EMAIL.replyTo),
    subscribeSubject: str(r.subscribeSubject, DEFAULT_EMAIL.subscribeSubject),
    newsletterFooter: str(r.newsletterFooter, DEFAULT_EMAIL.newsletterFooter),
  };
}

function normalizeSeo(raw: Partial<SeoSettings> | null | undefined): SeoSettings {
  const r = raw ?? {};
  const s = (r.social ?? {}) as Partial<SocialLinks>;
  return {
    titleSuffix: str(r.titleSuffix, DEFAULT_SEO.titleSuffix),
    defaultDescription: str(r.defaultDescription, DEFAULT_SEO.defaultDescription),
    defaultShareImage: str(r.defaultShareImage, DEFAULT_SEO.defaultShareImage),
    social: {
      linkedin: str(s.linkedin, ""),
      x: str(s.x, ""),
      facebook: str(s.facebook, ""),
      instagram: str(s.instagram, ""),
      youtube: str(s.youtube, ""),
    },
    searchConsoleTag: str(r.searchConsoleTag, DEFAULT_SEO.searchConsoleTag),
  };
}

function normalizeIntegrations(
  raw: Partial<IntegrationSettings> | null | undefined,
): IntegrationSettings {
  const r = raw ?? {};
  const f = (r.turnstileEnabledForms ?? {}) as Partial<TurnstileEnabledForms>;
  return {
    turnstileSiteKey: str(r.turnstileSiteKey, DEFAULT_INTEGRATIONS.turnstileSiteKey),
    turnstileEnabledForms: {
      contact: bool(f.contact, DEFAULT_INTEGRATIONS.turnstileEnabledForms.contact),
      newsletter: bool(f.newsletter, DEFAULT_INTEGRATIONS.turnstileEnabledForms.newsletter),
    },
  };
}

function normalizeAnalytics(
  raw: Partial<AnalyticsSettings> | null | undefined,
): AnalyticsSettings {
  const r = raw ?? {};
  return {
    ga4MeasurementId: str(r.ga4MeasurementId, DEFAULT_ANALYTICS.ga4MeasurementId),
    plausibleDomain: str(r.plausibleDomain, DEFAULT_ANALYTICS.plausibleDomain),
    consentBannerEnabled: bool(r.consentBannerEnabled, DEFAULT_ANALYTICS.consentBannerEnabled),
    consentText: str(r.consentText, DEFAULT_ANALYTICS.consentText),
    excludeAdmin: bool(r.excludeAdmin, DEFAULT_ANALYTICS.excludeAdmin),
  };
}

/** Fills any group or field the backend left out with its default. */
export function normalizeSettings(
  raw: Partial<SettingsResponse> | null | undefined,
): SettingsResponse {
  const r = raw ?? {};
  return {
    org: normalizeOrg(r.org),
    notifications: normalizeNotifications(r.notifications),
    email: normalizeEmail(r.email),
    seo: normalizeSeo(r.seo),
    integrations: normalizeIntegrations(r.integrations),
    analytics: normalizeAnalytics(r.analytics),
    updatedAt: r.updatedAt ?? {},
    senderVerified: !!r.senderVerified,
  };
}

export function normalizePublicSettings(
  raw: Partial<PublicSettings> | null | undefined,
): PublicSettings {
  const r = raw ?? {};
  return {
    org: normalizeOrg(r.org),
    seo: normalizeSeo(r.seo),
    email: { senderName: str(r.email?.senderName, DEFAULT_EMAIL.senderName) },
    analytics: normalizeAnalytics(r.analytics),
    integrations: normalizeIntegrations(r.integrations),
  };
}

/* ----------------------------------------------------------------- calls */

/** Admin. All groups plus save times and the SES verification flag. */
export const getSettings = async (): Promise<SettingsResponse> =>
  normalizeSettings(await fetchApi<Partial<SettingsResponse>>("admin", "getSettings", {}));

/**
 * Admin. Replaces one group. A validation failure throws an ApiError with
 * `code: "VALIDATION"` and `fields: { "<path>": "<message>" }` (paths are
 * relative to the group, e.g. "contactRecipients" or "social.linkedin").
 */
export const updateSettings = async <G extends SettingsGroup>(params: {
  group: G;
  value: SettingsGroupValue<G>;
}): Promise<SettingsResponse> =>
  normalizeSettings(await fetchApi<Partial<SettingsResponse>>("admin", "updateSettings", params));

/** Admin. Sends a sample notification to the configured recipients of that kind. */
export const sendTestAlert = (params: { kind: "contact" | "health" }) =>
  fetchApi<{ ok: boolean; to: string[] }>("admin", "sendTestAlert", params);

/* ------------------------------------------------ integrations (admin) */

const isoOrNull = (v: unknown): string | null => (typeof v === "string" && v ? v : null);

function normalizeProbe(raw: Partial<ProbeResult> | null | undefined): ProbeResult {
  const r = raw ?? {};
  return { ok: !!r.ok, detail: str(r.detail, ""), checkedAt: str(r.checkedAt, "") };
}

/** Coerces a per-key "configured" flag: a boolean, or a non-empty id string. */
const configuredFlag = (v: unknown): boolean =>
  typeof v === "boolean" ? v : typeof v === "string" ? v.trim().length > 0 : !!v;

export function normalizeIntegrationStatus(
  raw: Partial<IntegrationStatus> | null | undefined,
): IntegrationStatus {
  const r = raw ?? {};
  const sh = (r.shopify ?? {}) as Partial<IntegrationStatus["shopify"]>;
  const admin = (sh.admin ?? {}) as Partial<IntegrationStatus["shopify"]["admin"]>;
  const ts = (r.turnstile ?? {}) as Partial<IntegrationStatus["turnstile"]>;
  const an = (r.analytics ?? {}) as Partial<Record<"ga4" | "plausible", unknown>>;
  return {
    shopify: {
      storeDomain: str(sh.storeDomain, ""),
      apiVersion: str(sh.apiVersion, ""),
      storefront: normalizeProbe(sh.storefront),
      admin: {
        configured: !!admin.configured,
        ok: typeof admin.ok === "boolean" ? admin.ok : null,
        detail: str(admin.detail, ""),
        lastChangedAt: isoOrNull(admin.lastChangedAt),
      },
    },
    turnstile: {
      siteKeyConfigured: !!ts.siteKeyConfigured,
      secretConfigured: !!ts.secretConfigured,
      lastChangedAt: isoOrNull(ts.lastChangedAt),
    },
    analytics: { ga4: configuredFlag(an.ga4), plausible: configuredFlag(an.plausible) },
  };
}

/** Admin. Live status of every third-party hookup; never returns a secret. */
export const getIntegrationStatus = async (): Promise<IntegrationStatus> =>
  normalizeIntegrationStatus(
    await fetchApi<Partial<IntegrationStatus>>("admin", "getIntegrationStatus", {}),
  );

/** Admin. Stores the Shopify Admin API token (write-only) and probes it right away. */
export const setShopifyAdminToken = (params: { token: string }) =>
  fetchApi<{ ok: boolean; lastChangedAt: string; probe: { ok: boolean; detail: string } }>(
    "admin",
    "setShopifyAdminToken",
    params,
  );

/** Admin. Stores the Turnstile secret key (write-only). */
export const setTurnstileSecret = (params: { secret: string }) =>
  fetchApi<{ ok: boolean; lastChangedAt: string }>("admin", "setTurnstileSecret", params);

/** Admin. Runs one connectivity probe and returns its result. */
export const testIntegration = async (params: {
  kind: IntegrationTestKind;
}): Promise<ProbeResult> =>
  normalizeProbe(await fetchApi<Partial<ProbeResult>>("admin", "testIntegration", params));

/** Public. The subset the site renders (footer, contact links, head meta, analytics, captcha). */
export const getPublicSettings = async (): Promise<PublicSettings> =>
  normalizePublicSettings(
    await fetchApi<Partial<PublicSettings>>("content", "getPublicSettings", {}, { auth: false }),
  );

/* --------------------------------------------------------------- helpers */

/** "Strategic Research — Medical Insight"; the bare suffix when no page title. */
export function pageTitle(
  base: string | null | undefined,
  seo: Pick<SeoSettings, "titleSuffix">,
): string {
  const suffix = seo.titleSuffix.trim();
  if (!base) return suffix;
  return suffix ? `${base} — ${suffix}` : base;
}

/** `mailto:` link to a settings-driven address with an optional subject. */
export function mailtoLink(email: string, subject?: string): string {
  return subject ? `mailto:${email}?subject=${encodeURIComponent(subject)}` : `mailto:${email}`;
}
